import { VATMessageEnum } from '../../../utils/enums';
import { isDirftPortLabel, NUTRICIA_EORI } from '../../../utils/constants';
import { getIsValidPreferenceTransitMrn } from '../../../utils/stringValidation';
import {
  getNewLoadQaVat,
  getExchangeRate,
  getDutyRates,
  postCheckPreferentialValidation,
  postNewLoad,
} from '../../apiCalls';

/** QA: Nutricia consignee must use DIRFT entry port and DIRFT must pair with Nutricia. */
export const isDirftNutriciaEntryPortCombinationInvalid = (entryPortLabel, consigneeEori, entryPortFieldVisible) => {
  if (!entryPortFieldVisible) return false;
  if (!entryPortLabel) return true;
  const isNutricia = consigneeEori === NUTRICIA_EORI;
  const isDirft = isDirftPortLabel(entryPortLabel);
  return isNutricia !== isDirft;
};

export const isChedDoc = (doc = {}) => {
  const lowerTitle = doc?.title?.toLowerCase?.() || '';
  const lowerForm = doc?.formname?.toLowerCase?.() || '';
  return ['ched', 'ipaffs', 'traces'].some((key) => lowerTitle.includes(key) || lowerForm.includes(key));
};

export const getChedQtyFromDocTypes = (docTypes = []) =>
  docTypes?.reduce((acc, doc) => {
    if (isChedDoc(doc) && doc?.checked) {
      return acc + (Number(doc.qty) || 1);
    }
    return acc;
  }, 0) || 0;

export const normalizePreferenceClaimOptions = (options = []) =>
  (options || []).map((opt) => {
    const preferenceId = opt.id ?? '';
    const preferenceCode = opt.preferenceCode ?? '';
    const label =
      opt.PreferenceClaimValue || opt.preferenceClaim || opt.label || opt.data || preferenceCode || preferenceId;
    const area = (opt.areacodeValue || opt.areacode || '').toString().toUpperCase();
    const region = area === 'EU' ? 'EU' : area === 'ROW' ? 'ROW' : '';
    return {
      ...opt,
      id: preferenceId,
      preferenceCode,
      label,
      region,
      data: label,
    };
  });

export const filterClaimsByRegion = (options, region) => {
  const target = region.toUpperCase();
  return options.filter((opt) => {
    const optRegion = (opt.region || '').toUpperCase();
    if (optRegion === 'BOTH' || optRegion === '') return true;
    return optRegion === target;
  });
};

export const fetchExchangeRate = async (currencyOptions, invoiceCurrId, loadingDate, setExchangeRate) => {
  if (!currencyOptions?.length || !invoiceCurrId) {
    return;
  }

  const currency = currencyOptions.find((c) => c.id === invoiceCurrId?.toString())?.data;

  if (!currency) {
    return;
  }

  if (currency === 'GBP') {
    setExchangeRate(1);
  } else {
    try {
      // only pass loading date to the backend if it is not the current month
      const todaysDate = new Date();
      const convertedLoadingDate = new Date(loadingDate);
      const getCurrentRate = convertedLoadingDate.getMonth() >= todaysDate.getMonth();

      const [data] = await getExchangeRate(currency, getCurrentRate ? null : loadingDate);
      setExchangeRate(data?.rate);
    } catch {
      setExchangeRate('ERROR');
    }
  }
};

export const fetchAdjExchangeRate = async (currencyOptions, currId, loadingDate, exchangeRateCacheState) => {
  if (!currencyOptions?.length || !currId) {
    return;
  }

  const currency = currencyOptions.find((c) => c.id === currId?.toString())?.data;

  if (!currency) {
    return;
  }

  const [exchangeRateCache, setExchangeRateCache] = exchangeRateCacheState;

  const cacheKey = `${currency}-${loadingDate}`;

  // if rate is already in cache, do not fetch it again
  if (exchangeRateCache?.[cacheKey]) {
    return exchangeRateCache[cacheKey];
  }

  if (currency === 'GBP') {
    return 1;
  } else {
    try {
      // only pass loading date to the backend if it is not the current month
      const todaysDate = new Date();
      const convertedLoadingDate = new Date(loadingDate);
      const getCurrentRate = convertedLoadingDate.getMonth() >= todaysDate.getMonth();

      const [data] = await getExchangeRate(currency, getCurrentRate ? null : loadingDate);
      setExchangeRateCache((prev) => ({ ...prev, [cacheKey]: data?.rate }));
      return data?.rate;
    } catch (error) {
      console.error('Error fetching exchangeRate for currency Id: ', currId, ' | ', error);
    }
  }
};

const roiToGBWithDocuments = (route) => {
  return route.isROIExport && route.documents.hasEAD && (route.documents.hasFFD || route.documents.hasGBImport);
};

const roiToGBWithNoImportDocument = (route) => {
  // For CABS-2462 - to identify ROI to GB routes with EAD but no import documents selected
  return route.isROIExport && route.documents.hasEAD && !(route.documents.hasFFD || route.documents.hasGBImport);
};

export const getQAConfig = (route) => {
  // default config
  const defaultConfig = {
    visibleFields: {
      entryPort: true,
      exitPort: true,
      adjustments: true,
      type: true,
      preference: true,
      financialsTable: true,
      route: true,
      loadingDate: true,
      incoterm: true,
      portModel: true,
      invoiceCurrency: true,
      invoiceLines: true,
      invoiceHeaders: true,
      potentialLiability: true,
      customsMop: true,
      vat: true,
      tariffModal: {
        thirdCountryDutyAdValorem: false,
        thirdCountryDutySpecific: false,
        duty: false,
        quotas: false,
        remedies: false,
        vat: false,
        excise: false,
        customsTech: true,
      },
    },
    consignorOptions: {
      useBusinessPartners: false,
    },
    consigneeOptions: {
      useBusinessPartners: true,
    },
    hideItemColumns: {
      vatLiability: false,
      dutyLiability: false,
      preference: false,
    },
    hideTotalValues: {
      totalDuty: false,
      totalVAT: false,
    },
    vat: {
      rate: 0.2,
    },
    documents: route?.documents || [],
  };
  if (!route) return defaultConfig;
  const ROIToGBWithDocuments = roiToGBWithDocuments(route);

  if (route.isROIImport && route.documents.hasEUImportEntry) {
    //VAT is different for ROI
    defaultConfig.vat.rate = 0.23;

    //CABS-2365 Update Financials on QA screens for EU Import Entries
    defaultConfig.visibleFields.potentialLiability = false;
    defaultConfig.hideItemColumns.vatLiability = true;
    defaultConfig.hideItemColumns.dutyLiability = true;
    defaultConfig.hideTotalValues.totalDuty = true;
    defaultConfig.hideTotalValues.totalVAT = true;
  }

  if (route.isGBImport) {
    defaultConfig.visibleFields.exitPort = false;

    //cabs-1605 and cabs-2462 - tariff details (excl. ROI exports with only EAD selected)
    if (!roiToGBWithNoImportDocument(route)) {
      defaultConfig.visibleFields.tariffModal.thirdCountryDutyAdValorem = true;
      defaultConfig.visibleFields.tariffModal.thirdCountryDutySpecific = true;
      defaultConfig.visibleFields.tariffModal.duty = true;
      defaultConfig.visibleFields.tariffModal.quotas = true;
      defaultConfig.visibleFields.tariffModal.remedies = true;
      defaultConfig.visibleFields.tariffModal.vat = true;
      defaultConfig.visibleFields.tariffModal.excise = true;
    }
  }

  if (!route.isGBImport) {
    // for GB exports
    defaultConfig.visibleFields.entryPort = false;
    defaultConfig.visibleFields.exitPort = true;
  }

  if (ROIToGBWithDocuments) {
    // for ROI to GB routes, with EAD and import documents selected
    defaultConfig.visibleFields.exitPort = true;
    defaultConfig.consignorOptions.useBusinessPartners = true;
  }

  if ((route.isROIExport || !route.isGBImport) && !ROIToGBWithDocuments) {
    defaultConfig.consignorOptions.useBusinessPartners = true;
    defaultConfig.consigneeOptions.useBusinessPartners = false;
  }

  //CABS-1836 - if EAD is selected and no import order forms are selected
  //CABS-2616 - changed so that financials table is now visible, but with liability and customsMop hidden
  if (
    route.documents.hasEAD &&
    !route.documents.hasGBImport &&
    !route.documents.hasEUImportEntry &&
    !route.documents.hasFFD
  ) {
    defaultConfig.visibleFields.entryPort = false;
    defaultConfig.visibleFields.adjustments = false;
    defaultConfig.visibleFields.type = false;
    defaultConfig.visibleFields.preference = false;
    defaultConfig.visibleFields.potentialLiability = false;
    defaultConfig.visibleFields.customsMop = false;

    // changes requested specifically for CABS-2616
    defaultConfig.visibleFields.vat = false;
    defaultConfig.visibleFields.exitPort = true;
    defaultConfig.hideItemColumns.dutyLiability = true;
    defaultConfig.hideItemColumns.vatLiability = true;
    defaultConfig.hideTotalValues.totalDuty = true;
    defaultConfig.hideTotalValues.totalVAT = true;
  }

  if (route.isROIImport && route.documents.hasEUImportEntry && !route.documents.hasEAD) {
    defaultConfig.visibleFields.type = false;
    defaultConfig.visibleFields.preference = false;

    defaultConfig.visibleFields.exitPort = false;
    defaultConfig.visibleFields.entryPort = true;

    defaultConfig.consignorOptions.useBusinessPartners = false;
    defaultConfig.consigneeOptions.useBusinessPartners = true;
  }

  //cabs-2184 - ROI export (ROI-GB, ROI-GB via NI)
  /* Given I have selected a GB Import Entry
  Then the consignee should look up to Business Partners*/
  if (route.isROIExport && (route.documents.hasFFD || route.documents.hasGBImport)) {
    defaultConfig.consigneeOptions.useBusinessPartners = true;
  }

  //cabs-2184 - ROI export (ROI-GB, ROI-GB via NI)
  /* Given I have selected a GB Import Entry AND not selected an Export (EAD)
  Then the consignor should look up to the NeeNor table*/
  if (route.isROIExport && !route.documents.hasEAD && (route.documents.hasFFD || route.documents.hasGBImport)) {
    defaultConfig.consignorOptions.useBusinessPartners = false;
  }

  //cabs 1865 && 1866 && 1837
  //CABS-2365 - leaving customsMop and vat visible
  if (route.isROIImport && route.documents.hasEAD && route.documents.hasEUImportEntry) {
    defaultConfig.consignorOptions.useBusinessPartners = true;
    defaultConfig.consigneeOptions.useBusinessPartners = true;

    defaultConfig.visibleFields.potentialLiability = false;

    //entry port was hidden in a previous rule, but we need it for this one
    defaultConfig.visibleFields.entryPort = true;
  }

  // General rule: For any EU Import Entry document, hide Preference and Type in Compliance Checklist
  if (route.documents.hasEUImportEntry) {
    defaultConfig.visibleFields.type = false;
    defaultConfig.visibleFields.preference = false;
  }

  // CABS-2582 - Allow SA on neenor, show neenor data for consignees/consignors
  if (route.hasRepresentative && route.allowSAOnNeeNors) {
    defaultConfig.consignorOptions.useBusinessPartners = false;
    defaultConfig.consigneeOptions.useBusinessPartners = false;
  }

  return defaultConfig;
};

export const getConfigRoute = (
  isGBImport,
  direction,
  selectedForms,
  selectedDocuments,
  representative,
  allowSAOnNeeNors,
  incoterm = ''
) => {
  const hasEAD = containsDocType('docTypeexport', selectedForms, selectedDocuments);
  const hasGBImportEntry = containsDocType('docTypegbimp', selectedForms, selectedDocuments);
  const hasFFD = containsDocType('docTypeffd', selectedForms, selectedDocuments);
  const hasEUImportEntry = containsDocType('docTypeeuimp', selectedForms, selectedDocuments);
  const hasAnyImport = hasEUImportEntry || hasGBImportEntry || hasFFD;
  const hasAnyExport = hasEAD;

  const documents = {
    hasEAD: hasEAD,
    hasGBImport: hasGBImportEntry,
    hasFFD: hasFFD,
    hasEUImportEntry: hasEUImportEntry,
    hasAnyImport: hasAnyImport,
    hasAnyExport: hasAnyExport,
  };

  const isEUImport = [3].includes(parseInt(direction)); // GB - EU
  const isROIImport = [6, 7].includes(parseInt(direction)); // GB - ROI; GB - ROI via NI
  const isROIExport = [10, 11].includes(parseInt(direction));

  return {
    isGBImport: isGBImport,
    isEUImport: isEUImport,
    isROIImport: isROIImport,
    isROIExport: isROIExport,
    direction: direction,
    documents: documents,
    incoterm: incoterm,
    hasRepresentative: representative,
    allowSAOnNeeNors,
  };
};

const containsDocType = (docType, selectedForms = [], selectedDocuments = []) => {
  // selectedForms are forms returned from API on initial load
  // selectedDocuments are documents user has selected from Order Form page (empty on initial load)
  // here we are checking of docType is visible in either of these two arrays
  if (selectedDocuments?.length) {
    return selectedDocuments.some((doc) => doc.checked && doc.formname === docType);
  }

  return selectedForms?.includes(docType);
};

export const getValueFromID = (options, id) => {
  const selectedOption = options?.find((option) => parseInt(option.id) === parseInt(id));
  return selectedOption ? selectedOption.data : null;
};

export const extractAndRenameByPrefix = (obj, prefix) => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, value]) => {
        const stripped = key.slice(prefix.length); // Remove the prefix
        const newKey = stripped.charAt(0).toLowerCase() + stripped.slice(1); // Lowercase first letter
        return [newKey, value];
      })
  );
};
export const formatCompliancePersistedData = (persistedData, companyType, companyName) => {
  const newPersistedData = extractAndRenameByPrefix(persistedData, companyType);
  return { ...newPersistedData, companyName };
};

//`VATS` (as opposed to `VATZ`) can be represented by the number 1 or the string VATS
const VATS_STRING = 'VATS';
const VATS_NUMBER = 2;
export const getIsVatLiable = (vatOption) => {
  if (typeof vatOption === 'string' || vatOption instanceof String) {
    return vatOption.toUpperCase() === VATS_STRING;
  }

  return vatOption === VATS_NUMBER;
};

export const checkIfConsignorIsValid = (config, incoterm, consignorStatus, customerData, representative) => {
  const validateAllowSAOnNeenors = allowSAOnNeenorsValidation(customerData, representative);

  return (
    validateAllowSAOnNeenors ||
    !config.consignorOptions.useBusinessPartners ||
    !!(
      consignorStatus.cabieStatusValid &&
      consignorStatus.dbStatusValid &&
      consignorStatus.standingAuthorityValid &&
      (incoterm !== 'EXW' || consignorStatus.exWorksValid)
    )
  );
};

export const checkIfConsigneeIsValid = (consigneeStatus, customerData, representative) => {
  const validateAllowSAOnNeenors = allowSAOnNeenorsValidation(customerData, representative);

  return (
    validateAllowSAOnNeenors ||
    !!(consigneeStatus?.cabieStatusValid && consigneeStatus?.dbStatusValid && consigneeStatus?.standingAuthorityValid)
  );
};

export const checkIfCustomerIsValid = (invoiceModalData) => {
  return invoiceModalData.cabieStatusValid && invoiceModalData.dbStatusValid;
};

const allowSAOnNeenorsValidation = (customerData, representative) => {
  const customerSAValid = customerData?.validStandingAuthority;
  const customerAllowSAOnNeenors = customerData?.allowSAOnNeeNors;

  if (customerSAValid && customerAllowSAOnNeenors && representative) return true;

  return false;
};

export const fetchRevenuePayableData = async (
  id,
  setQaVat,
  {
    customerAllowSAOnNeeNors = false,
    isConsigneeBP = false,
    vatTotal = 0,
    isGBImport = false,
    hasEUImportEntry = false,
    orderFromRepresentative = false,
    isAllVatZ = false,
    oneOrMoreVatS = false,
    isPVA = false,
  } = {}
) => {
  const { CustomsMOP, NotAvailable, NULL, PVA } = VATMessageEnum;

  try {
    const data = await getNewLoadQaVat(id);
    let vatMessage = data[0].vatMessage;
    let vatValid = data[0].vatValid;

    if (vatTotal === 0 && (customerAllowSAOnNeeNors || isConsigneeBP)) {
      vatValid = true;
      vatMessage = NotAvailable;
    }
    if (isGBImport) {
      vatValid = true;
      if (vatTotal === 'CALCULATION' && isPVA) {
        vatMessage = PVA;
      } else if (Number(vatTotal) === 0) {
        vatMessage = NotAvailable;
      } else if ((Number(vatTotal) > 0 || vatTotal === 'CALCULATION') && isConsigneeBP && !isPVA) {
        vatMessage = CustomsMOP;
      } else if (!isConsigneeBP && !orderFromRepresentative) {
        vatMessage = NULL;
      }
    } else if (hasEUImportEntry) {
      vatValid = true;
      if (vatTotal === 'CALCULATION' && isPVA) {
        vatMessage = PVA;
      } else if (isAllVatZ) {
        vatMessage = NotAvailable;
      } else if (oneOrMoreVatS && !isPVA) {
        vatMessage = CustomsMOP;
      } else if (!isConsigneeBP && !orderFromRepresentative) {
        vatMessage = NULL;
      }
    }
    setQaVat({
      id: data[0].id,
      vatMessage,
      vatValid,
    });
  } catch (error) {
    console.error('Error fetching consignee data from getNewLoadQaVat for consignee: ', id, ' | ', error);
  }
};

export const getHeaderValues = (products, origin = 'QA') => {
  switch (origin) {
    case 'QA':
      return new Set(products.map((product) => `${product.commoditycode}${product.origin}`))?.size;
    case 'loadItems':
      return new Set(products.map((product) => `${product['HS Code (10)']}${product['Origin']}`))?.size;
    default:
      return new Set(products.map((product) => `${product[0].text}${product[1].text}`))?.size;
  }
};

export const convertToGbp = (amount, exchangeRate) => {
  return exchangeRate ? amount / exchangeRate : null;
};

export const getFormDataToOrderForms = (store, roleValidated) => {
  const anyCerts = store.validatedRowsReadyToSubmit[0].some((obj) =>
    typeof obj.healthCert === 'string'
      ? obj.healthCert.length > 5 &&
        obj.healthCert.toUpperCase() !== 'NOT REQUIRED' &&
        obj.healthCert.toUpperCase() !== 'EXEMPT'
      : null
  );
  const chedQty = getChedQtyFromDocTypes(store.docTypes[0]);
  const postData = new FormData();
  postData.append('directionid', store.directionSelected[0] ?? '');

  const defaultControlledGoodsValue = roleValidated ? 'Y' : 'N'; //if no value is selected on the 'Header' tab, then default to 'Y' for QA users, 'N' for other users
  const currentControlledGoodsValue =
    store.controlledGoods[0] === 'false' ? defaultControlledGoodsValue : store.controlledGoods[0]; // fix - because controlledGoods can also be stored as 'false' in the DB
  postData.append('controlledGoods', currentControlledGoodsValue || defaultControlledGoodsValue);

  postData.append('healthCert', anyCerts ? 'Yes' : 'No'); // TODO: may change to "YES", "NO", or something else.
  postData.append('officeOfTransit', store.officeOfDest[0] ? 'Yes' : 'No');

  postData.append('port1', store.RowImport[0] ? '' : store.exit[0]);
  postData.append('port2', store.RowExport[0] ? '' : store.entry[0]);

  postData.append('port3', store.exit2[0] ?? '');
  postData.append('port4', store.entry2[0] ?? '');

  postData.append('gvms', store.portsModal[0] ?? '');
  postData.append('commCodes', JSON.stringify(store.validatedRowsReadyToSubmit[0]));
  postData.append('consignorEori', store.consignor[0] || '');
  postData.append('consigneeEori', store.consignee[0] || '');
  postData.append('incoterm', +store.selectincoterm[0]);
  postData.append('shipStatus', store.shipStatus[0] || 0);
  postData.append('clientID', store.clientID[0]);
  postData.append('qTracesChed', chedQty);
  return postData;
};

export const removeEmptyOrNull = (obj) => {
  Object.keys(obj).forEach(
    (k) =>
      (obj[k] && typeof obj[k] === 'object' && removeEmptyOrNull(obj[k])) ||
      (!obj[k] && obj[k] !== undefined && delete obj[k]) ||
      (typeof obj[k] === 'string' && obj[k].trim() === '' && delete obj[k])
  );
  return obj;
};

export const prepareItemsListToValidateOnQA = (spreadsheetHandle, store) => {
  // Collapses any completely empty rows and squeezes non-empty rows together.
  const collapsedFiles = [];
  let collapsedRows = [];

  for (const idx in spreadsheetHandle.current.excelState.rows) {
    const row = spreadsheetHandle.current.excelState.rows[idx];
    if (row.some((cell) => cell.text !== '')) {
      collapsedRows.push(row);
      collapsedFiles.push(spreadsheetHandle.current.excelState.files[idx]);
    }
  }

  // Sets default values for any columns that require it.
  collapsedRows = collapsedRows.map((row) => {
    const new_row = [];
    spreadsheetHandle.current.excelState.config.forEach((col, idx) => {
      const { default_text } = col;
      new_row.push({
        ...row[idx],
        text: (row[idx]?.text || default_text) ?? '',
      });
    });
    return new_row;
  });

  // Uses configuration 'keys' to construct array of item objects.

  const items = collapsedRows.map((row, row_idx) => {
    const obj = {};
    spreadsheetHandle.current.excelState.config.forEach((col, idx) => {
      const { key } = col;
      obj[key] = row[idx]?.text ?? '';
      if (row[idx]?.id !== undefined) {
        obj['id'] = row[idx].id;
      }
    });

    // -- TODO: if dummy value is provided by backend, remove the below.
    if (!obj['commoditycode'] && store.tadEns[0]) {
      obj['commoditycode'] = null;
    }
    // --

    obj.file_ids = (collapsedFiles[row_idx] ?? []).map((file) => file.id);
    return obj;
  });

  // Backend doesn't like empty strings or nulls, prefered straight 'undefined'.
  const itemsWithoutMovement = removeEmptyOrNull(items).filter((value) => Object.keys(value).length !== 0);

  const filteredList = itemsWithoutMovement.map((x) => ({
    ...x,
    movementIdSelected: store.directionSelected[0],
    despatchIdSelected: store.despatch[0],
    loadValue: convertToGbp(x.itemValue, store.exchangeRate[0]),
  }));

  return filteredList;
};

export const areObjectsDifferent = (currentValue, filteredList) => {
  return JSON.stringify(currentValue) !== JSON.stringify(filteredList);
};

export const qaDutyLoginRequired = ({
  potentialLiability,
  isDutyUser,
  isGbImport,
  isControlled,
  isRowToGB,
  selectedDocuments,
  portValue,
  hasExciseGoods,
}) => {
  const hasGbImportEntry = containsDocType('docTypegbimp', [], selectedDocuments);
  const hasFFD = containsDocType('docTypeffd', [], selectedDocuments);

  const defaultResponse = {
    BLOCKED: false,
    LOGIN: false,
    OK: false,
  };

  if (isDutyUser) {
    return { ...defaultResponse, OK: true };
  }

  if (!potentialLiability || potentialLiability === '-') {
    if (hasExciseGoods) {
      return { ...defaultResponse, BLOCKED: true };
    }
    return { ...defaultResponse, OK: true };
  } else {
    if (hasFFD) {
      return { ...defaultResponse, BLOCKED: true };
    }
    if (isGbImport && isControlled) {
      return { ...defaultResponse, LOGIN: true };
    }
    if (isRowToGB && hasGbImportEntry && portValue === 'Inventory Linked') {
      return { ...defaultResponse, LOGIN: true };
    }
    if (isGbImport && !isControlled) {
      return { ...defaultResponse, BLOCKED: true };
    }
  }

  return { ...defaultResponse, OK: true };
};

const isEpoItemPreferentialYes = (item) => item && ['YES', '1', 1, true].includes(item.preferential);

export const parseDutyRateNumeric = (raw) => {
  if (raw == null || raw === '' || raw === false) return null;
  const s = String(raw).replaceAll('%', '').trim();
  if (s === '' || s === '-') return null;
  const n = Number.parseFloat(s);
  if (Number.isNaN(n)) return null;
  return n;
};

/**
 * Third-country duty rate &gt; 0 from commodityDutyRates (Trade Tariff / third country fields).
 * Used for: Pref No + show duty rate in modal, and including the line when no other duty flags apply.
 */
export const hasThirdCountryDutyRateGreaterThanZero = (duty) => {
  if (!duty) return false;
  const fields = [
    duty.dutyExpression,
    duty.DutyExpression,
    duty.thirdCountryAV,
    duty.thirdCountrySpecific,
    duty.dutyExpressionRate,
  ];
  for (const f of fields) {
    const n = parseDutyRateNumeric(f);
    if (n != null && n > 0) return true;
  }
  return false;
};

const quotaFieldIsApplicable = (value) => {
  if (value == null || value === '' || value === false) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  if (typeof value === 'string') {
    const t = value.trim().toLowerCase();
    return !['false', '0', 'n', 'no'].includes(t);
  }
  return Boolean(value);
};

/**
 * True when commodityDutyRates indicates quotas may apply for this line.
 * Backend JSON may use PascalCase (`Quotas`) or camelCase (`quotas`).
 */
export const dutyHasQuotasApplicable = (duty) => {
  if (!duty) return false;
  const candidates = [
    duty.Quotas,
    duty.quotas,
    duty.Quota,
    duty.quota,
    duty.hasQuota,
    duty.HasQuota,
    duty.quotaApplicable,
    duty.QuotaApplicable,
  ];
  return candidates.some(quotaFieldIsApplicable);
};

/**
 * EPO duty-check modal row: line is listed if any AC applies:
 * - third-country duty &gt; 0 with Pref No
 * - Quota with Pref No
 * - trade remedies (API/origin)
 * - excise
 * - customs tech
 * - positive preferentialDutyRate with pref yes.
 */
export const epoSummaryLineNeedsDutyCheck = (duty, item) => {
  if (!duty) return false;

  if (duty.excise != null && duty.excise !== '' && duty.excise !== false) return true;
  if (duty.additionalDuties != null && duty.additionalDuties !== '' && duty.additionalDuties !== false) return true;
  if (duty.customsTech === true) return true;

  if (isEpoItemPreferentialYes(item)) {
    const preferentialDutyRate = parseDutyRateNumeric(duty.dutyExpressionRate);
    return preferentialDutyRate !== null && preferentialDutyRate > 0;
  }

  if (dutyHasQuotasApplicable(duty)) return true;

  if (hasThirdCountryDutyRateGreaterThanZero(duty)) return true;

  return false;
};

/** EPO summary-tab items aligned with `getDutyRatesFromItems` / commodityDutyRates response order. */
export const mergeEpoDutyCheckRows = (items, dutyRates) => {
  if (!items?.length) return [];
  return items
    .map((item, idx) => ({ item, duty: dutyRates?.[idx] ?? null }))
    .filter(({ item, duty }) => epoSummaryLineNeedsDutyCheck(duty, item))
    .map(({ item, duty }) => ({ ...item, duty }));
};

export const getDutyRatesFromItems = async (
  items,
  exchangeRate,
  vatAdj = null,
  roiImport = null,
  hasFFDOrGBImport = null
) => {
  if (!items) {
    return { error: true };
  }

  try {
    const itemsForDuty = items?.map((item, idx) => {
      //value is called itemValue in cabie, and loadValue in EPO
      const value = hasFFDOrGBImport ? item.customsValue : convertToGbp(item.itemValue || item.loadValue, exchangeRate);
      const prefValue = ['YES', '1', 1].includes(item.preferential) ? 'YES' : 'NO';

      return {
        itemId: idx + 1,
        // This is called commoditycode in cabie loads but commcode in epo loads
        commcode: item.commoditycode || item.commcode,
        origin: item.origin,
        grossMass: item.grossMass,
        netMass: item.netMass,
        loadValue: value,
        preferential: prefValue,
        exciseProductType: item.exciseProductType,
        exciseTaxCode: item.exciseTaxCode,
      };
    });

    if (!!itemsForDuty.length) {
      const data = await getDutyRates(itemsForDuty, vatAdj, roiImport);
      if (!Array.isArray(data)) {
        throw new Error('Invalid duty rate data response for these items');
      }
      return { error: false, data };
    }
    return { error: false, data: [] };
  } catch (e) {
    console.error(e);
    return { error: true };
  }
};

/* calculates the total invoice value for a given invoice number, by summing the item values of all products with that invoice number. */
export const calculateInvoiceTotal = (products, invoiceNumber) => {
  const value = products
    .filter((product) => product.invoiceNumber === invoiceNumber)
    .reduce((total, product) => total + (Number(product.value) || 0), 0);
  return value;
};

// Keeps only preferences that match at least one product by invoice number and origin.
export const filterPrefsByInvoiceOrigin = (preferenceData, products, loadInvoiceNumber) => {
  const hasSingleInvoiceNumber = !!loadInvoiceNumber;

  return preferenceData.filter((pref) =>
    products.some(
      (product) =>
        (product.invoiceNumber === pref.invoiceNumber || hasSingleInvoiceNumber) && product.origin === pref.origin
    )
  );
};

export const customsValue = (
  value,
  exchangeRate,
  freight,
  insurance,
  other,
  invoice,
  freightCurrency,
  insuranceCurrency,
  otherCurrency,
  invoiceCurrency,
  totalItem
) => {
  const newValue = Number(value);
  const exValue = Number(convertToGbp(newValue, exchangeRate));
  const freightCharges = Number(convertToGbp(freight, freightCurrency));
  const insuranceCharges = Number(convertToGbp(insurance, insuranceCurrency));
  const otherCharges = Number(convertToGbp(other, otherCurrency));
  const invoiceDiscount = Number(convertToGbp(invoice, invoiceCurrency));
  const totalItemValue = Number(convertToGbp(totalItem, exchangeRate));
  const ratio = exValue / totalItemValue;
  const freightAllocation = ratio * freightCharges;
  const insuranceAllocation = ratio * insuranceCharges;
  const otherChargesAllocation = ratio * otherCharges;
  const invoiceDiscountAllocation = ratio * invoiceDiscount;
  const sum = exValue + freightAllocation + insuranceAllocation + otherChargesAllocation - invoiceDiscountAllocation;
  return sum;
};

// Utility function to calculate VAT liability
export const calculateVatLiability = (dutyRates, idx, isVatLiable, vatLiability, readOnly = false) => {
  if (readOnly) return vatLiability;
  const vatAmount = dutyRates?.[idx]?.vatLiability ?? null;
  return isVatLiable ? vatAmount : '-';
};

// Utility function to calculate Duty liability
export const calculateDutyLiability = (dutyRates, idx, dutyLiability, readOnly = false) => {
  if (readOnly) return dutyLiability;
  return dutyRates?.[idx]?.dutyLiability ?? null;
};

export const getPortType = (options, id) => {
  const selectedOption = options?.find((option) => parseInt(option.id) === parseInt(id));
  return selectedOption ? parseInt(selectedOption?.hybrid) : null;
};

export const getPortName = (options, id) => {
  const selectedOption = options?.find((option) => parseInt(option.id) === parseInt(id));
  return selectedOption ? selectedOption.data : null;
};

export const fetchCheckModalData = async (data) => {
  const response = await postCheckPreferentialValidation(JSON.stringify(data));
  const euCountries = response.filter((c) => c.pref && c.eu).map((c) => c.origin);
  const thirdCountries = response.filter((c) => c.pref && c.thirdCountry).map((c) => c.origin);
  return { euCountries, thirdCountries };
};

export const validatePreferenceData = (preferenceData, products, loadInvoiceNumber, exchangeRate) => {
  let isValid = true;

  const normalisedProducts = products.map((product) => ({
    ...product,
    invoiceNumber: product.invoiceNumber || loadInvoiceNumber.toUpperCase() || 'N/A',
    value: product.itemValue || product.value || 0,
  }));

  // get only products which has preference YES
  const preferenceProducts = normalisedProducts.filter(
    (product) => String(product.pref) === '1' || product.preferential === 'YES' || product.preference === 'YES'
  );

  // Check to see if any products do not have any preference data at all
  for (const product of preferenceProducts) {
    const matchingPreference = preferenceData.find(
      (pref) =>
        pref.invoiceNumber.toUpperCase() === product.invoiceNumber.toUpperCase() && pref.origin === product.origin
    );
    if (!matchingPreference) {
      isValid = false;
    }
  }

  // Loop through preference data, and validate it
  for (const row of preferenceData) {
    const prefSelected = !!row.EUPreferenceClaim || !!row.thirdPreferenceClaim;

    const value = calculateInvoiceTotal(normalisedProducts, row.invoiceNumber);
    const statValue = convertToGbp(value, exchangeRate);

    const isTextFieldValid = row.EUPreferenceClaim
      ? statValue < 5400 || !!row.rexNumber
      : getIsValidPreferenceTransitMrn(row.directTransRule);

    if (
      !prefSelected ||
      !isTextFieldValid ||
      (!!row.rexError && !!row.rexNumber) ||
      (!!row.directTransRuleError && !!row.directTransRule)
    ) {
      isValid = false;
    }
  }

  return isValid;
};

export const showCheckOrView = (typeRates) =>
  dutyHasQuotasApplicable(typeRates) || typeRates?.excise || typeRates?.customsTech || typeRates?.additionalDuties
    ? 'check'
    : 'view';

export const validateSpreadsheetData = async (spreadsheetHandle, store) => {
  try {
    const filteredList = prepareItemsListToValidateOnQA(spreadsheetHandle, store);
    let errors = await postNewLoad(
      {
        filteredList: filteredList,
        consignee: store.consignee[0],
        GBimport: store.GBimport[0],
        multipleConsignor: store.multipleConsignors[0],
        multipleInvoice: store.multipleInvoices[0],
      },
      'validation'
    );
    errors = errors.map((error, idx) => {
      const hasError = Object.keys(error).some((key) => {
        return key !== 'error' && key.toLowerCase().includes('error') && !!error[key];
      });

      return {
        ...error,
        itemNumber: idx,
        hasError,
      };
    });
    return errors;
  } catch (err) {
    console.error('Error fetching data:', err);
  }
};

export const getControlledGoodsError = (dutyRateData, docTypes, controlledGoods, spreadsheetHandle) => {
  const filteredDutyRates = dutyRateData?.filter((e) => {
    return dutyHasQuotasApplicable(e) || e.excise || e.additionalDuties;
  });
  const anyItemsWithExcise = filteredDutyRates?.some((item) => item.excise);
  const hasGBImportDoc = docTypes.some((doc) => doc.formname === 'docTypegbimp' && doc.checked);

  const keys = spreadsheetHandle?.current?.excelState?.config?.map((c) => c.key);
  const chedSpreadsheetIndex = keys?.indexOf('healthCert');

  const hasChedRef = spreadsheetHandle?.current?.excelState?.rows.some((row) =>
    row[chedSpreadsheetIndex]?.text?.startsWith('CHED')
  );

  if (controlledGoods !== 'Y' && Array.isArray(filteredDutyRates) && !!filteredDutyRates.length) {
    return "These goods could be controlled. Please check the 'Type' of goods selected on the header screen.";
  } else if (hasGBImportDoc && anyItemsWithExcise) {
    return "This shipment contains excise goods. Please check 'GB FFD Entry' has been selected on the Order form.";
  } else if (hasGBImportDoc && controlledGoods !== 'Y' && hasChedRef) {
    return 'This load contains SPS goods. Please change Controlled Goods to yes on the Header page';
  }
  return null;
};
